import { useCallback, useEffect, useRef, useState } from 'react'

import { claimActive, getActive, isGroupWarm, nextHoverId, releaseActive, registerAvoidZone } from './hoverStore'
import { HOVER_SPEED_DELAY } from './hoverTypes'
import type { HoverAnchorInput, HoverContent, HoverController, HoverOptions } from './hoverTypes'

export interface UseHoverResult extends HoverController {
  open: boolean
  anchor: HoverAnchorInput | null
  content: HoverContent | null
  bubbleProps: {
    open: boolean
    anchor: HoverAnchorInput | null
    content: HoverContent | null
    variant: NonNullable<HoverOptions['variant']>
    placement: NonNullable<HoverOptions['placement']>
    avoidZonesGroup?: string
    interactive?: boolean
    maxWidth?: number
    offset?: number
    bubbleClassName?: string
    onRequestClose?: () => void
  }
}

const DEFAULT_OPEN = 350
const DEFAULT_CLOSE = 0

export function useHover(options: HoverOptions = {}): UseHoverResult {
  const {
    variant = 'label',
    placement = 'auto',
    delay,
    speed,
    group,
    avoidZonesGroup,
    disabled = false,
    maxWidth,
    interactive = false,
    offset,
    bubbleClassName,
  } = options
  const speedDelay = speed ? HOVER_SPEED_DELAY[speed] : undefined
  const openDelayOverride = delay?.open ?? speedDelay?.open
  const closeDelayOverride = delay?.close ?? speedDelay?.close

  const [hoverId] = useState(nextHoverId)

  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<HoverAnchorInput | null>(null)
  const [content, setContent] = useState<HoverContent | null>(null)

  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = useCallback(() => {
    if (openTimer.current !== null) {
      clearTimeout(openTimer.current)
      openTimer.current = null
    }
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const doOpen = useCallback(() => {
    setOpen(true)
    claimActive({ id: hoverId, group })
  }, [group, hoverId])

  const doClose = useCallback(() => {
    setOpen(false)
    releaseActive(hoverId)
  }, [hoverId])

  const show = useCallback<HoverController['show']>(
    ({ anchor: nextAnchor, content: nextContent, immediate }) => {
      if (disabled) return
      clearTimers()
      setAnchor(nextAnchor)
      setContent(nextContent)
      const currentActive = getActive()
      const warm = isGroupWarm(group)
      const sameGroupActive = Boolean(group && currentActive?.group === group)
      const openDelay = immediate || warm || sameGroupActive ? 0 : openDelayOverride ?? DEFAULT_OPEN
      if (currentActive && currentActive.id !== hoverId) {
        releaseActive(currentActive.id)
      }
      if (openDelay <= 0) {
        doOpen()
      } else {
        openTimer.current = setTimeout(() => {
          openTimer.current = null
          doOpen()
        }, openDelay)
      }
    },
    [disabled, clearTimers, group, openDelayOverride, hoverId, doOpen],
  )

  const hide = useCallback(() => {
    clearTimers()
    const closeDelay = closeDelayOverride ?? DEFAULT_CLOSE
    if (closeDelay <= 0) {
      doClose()
      return
    }
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      doClose()
    }, closeDelay)
  }, [clearTimers, closeDelayOverride, doClose])

  useEffect(() => {
    return () => {
      clearTimers()
      releaseActive(hoverId)
    }
  }, [clearTimers, hoverId])

  const bubbleProps = {
    open,
    anchor,
    content,
    variant,
    placement,
    avoidZonesGroup,
    interactive,
    maxWidth,
    offset,
    bubbleClassName,
    onRequestClose: hide,
  }

  return {
    open,
    anchor,
    content,
    show,
    hide,
    isOpen: () => open,
    bubbleProps,
  }
}

export interface UseAvoidZoneOptions {
  group: string
  id: string
  ref: React.RefObject<Element | null>
  enabled?: boolean
}

export function useAvoidZone({ group, id, ref, enabled = true }: UseAvoidZoneOptions) {
  useEffect(() => {
    if (!enabled) return
    return registerAvoidZone(group, id, () => {
      const el = ref.current
      if (!el) return null
      return el.getBoundingClientRect()
    })
  }, [group, id, ref, enabled])
}
